import { FC, useCallback, useMemo } from "react";
import { useWatch, useFormContext } from "react-hook-form";

import { Button, Buttons, useRemoteTableDataSource } from "capitalrx-components-2.0/lib/general";
import { Textbox, SubmitButton, MultiSelectDataDropdown, MultiSelectDropdown } from "capitalrx-components-2.0/lib/forms";
import { Grid, Shelf, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";

import { getApiBaseUrl } from "../../../api";
import { useFailureHandler } from "../../../hooks";
import { PricingLevelsTypes } from "@/types";
import { Network, PlanBenefit } from "../../../api/interfaces";
import { PricingLevelFilterFormType } from "..";
import { providerAgreementType, agreementChannel, lineOfBusiness } from "../../../constants";
import { generateMultiSelectDropdownOptions } from "../../../utils";

interface PricingLevelFilterFormProps {
  submitForm: (data: PricingLevelFilterFormType) => Promise<void>;
}

export const PricingLevelFilterForm: FC<PricingLevelFilterFormProps> = (props) => {
  const { submitForm } = props;

  const failureHandler = useFailureHandler();
  const { columns321, isMobile } = useResponsive({
    isMobile: { desktop: false, mobile: true },
    columns321: {
      desktop: 4,
      tablet: 6,
      mobile: 12,
    },
  });
  const { reset, getValues, handleSubmit } = useFormContext();
  const clientId = useWatch({ name: "client_ids" });
  const external_network_ids = useWatch({ name: "external_network_ids" });

  const pricingLevelsDataSource = useRemoteTableDataSource<PricingLevelsTypes.PricingLevelAPIResult>(
    `${getApiBaseUrl()}/pricing_level/list`,
    (row) => row.pricing_level.client_id,
    {
      pageSize: 15,
      idFilterName: "client_ids",
      refetchInterval: 60000,
      refetchOnWindowFocus: false,
      onError: failureHandler,
    },
  );

  const networkDataSourceDropdownData = useRemoteTableDataSource<Network.NetworkAPIResult>(
    `${getApiBaseUrl()}/network/list`,
    (row) => row.network.external_network_id,
    {
      pageSize: 15,
      idFilterName: "external_network_ids",
      onError: failureHandler,
    },
  );

  const csidDataSourceDropdownData = useRemoteTableDataSource<PlanBenefit.PlanBenefitListResult>(
    `${getApiBaseUrl()}/plan_benefit/list`,
    (row) => row?.plan_benefit?.coverage_strategy_id,
    {
      pageSize: 15,
      idFilterName: "coverage_strategy_ids",
      onError: failureHandler,
      filter: {
        only_fields: ["coverage_strategy_id", "name"],
        group_by_coverage_strategy_id: true,
      },
    },
  );

  const csidlabelProvider = useCallback((row: PlanBenefit.PlanBenefitListResult) => {
    return `${row?.plan_benefit?.name} | ${row?.plan_benefit?.coverage_strategy_id}`;
  }, []);

  const networklabelProvider = useCallback((row: Network.NetworkAPIResult) => {
    return `${row.network.external_network_id} - ${row.network.name}`;
  }, []);

  const clientIdLabelProvider = useCallback((row: PricingLevelsTypes.PricingLevelAPIResult) => {
    return `${row.pricing_level.client_id}`;
  }, []);


  const resetForm = useCallback(() => {
    reset(
      Object.keys(getValues()).reduce<{ [k: string]: null }>((prev, next) => {
        prev[next] = null;
        return prev;
      }, {}),
    );
    handleSubmit(submitForm)();
  }, [getValues, handleSubmit, reset, submitForm]);

  const filters = useMemo(
    () => (
      <Grid>
        <Grid.Spread columns={columns321}>
          <MultiSelectDataDropdown.Field<PricingLevelsTypes.PricingLevelAPIResult>
            name="client_ids"
            label="Client ID(s)"
            testId="test__client_id"
            dataSource={pricingLevelsDataSource}
            searchField="client_id"
            optionLabelProvider={clientIdLabelProvider}
            placeholder="Select"
          />
          <Textbox.Field name="external_group_id" label="External Group ID" testId="test__external_group_id" />
          <Textbox.Field name="external_account_id" label="External Account ID" testId="test__external_account_id" />
          <MultiSelectDropdown.Field label="Line(s) of Business" name="lines_of_business" placeholder="Select">
            {generateMultiSelectDropdownOptions(lineOfBusiness)}
          </MultiSelectDropdown.Field>
          <Textbox.Field name="notes" label="Notes" testId="test__notes" />
          <MultiSelectDataDropdown.Field
            dataSource={csidDataSourceDropdownData}
            optionLabelProvider={csidlabelProvider}
            name="coverage_strategy_ids"
            searchField="full_text_search"
            testId="test__coverage-strategy-ids"
            label="Coverage Strategy ID(s)"
            placeholder="Select"
          />
          <MultiSelectDataDropdown.Field<Network.NetworkAPIResult>
            name="external_network_ids"
            label="Network(s)"
            testId="test__external_network_ids"
            dataSource={networkDataSourceDropdownData}
            searchField="external_network_id"
            optionLabelProvider={networklabelProvider}
            placeholder="Select"
          />
          <MultiSelectDropdown.Field label="Provider Agreement Type(s)"
            testId="test__provider_agreement_types" name="agreement_types" placeholder="Select">
            {generateMultiSelectDropdownOptions(providerAgreementType)}
          </MultiSelectDropdown.Field>
          <MultiSelectDropdown.Field label="Provider Agreement Channel(s)"
            testId="test__agreement_channels" name="agreement_channels" placeholder="Select">
            {generateMultiSelectDropdownOptions(agreementChannel)}
          </MultiSelectDropdown.Field>
        </Grid.Spread>
      </Grid>
    ),
    [clientIdLabelProvider, pricingLevelsDataSource, clientId, external_network_ids],
  );

  return (
    <Stack>
      {filters}
      {!isMobile && (
        <Stack spacing="mini" alignment="left">
          <Shelf spacing="small">
            <SubmitButton appearance={Buttons.Functional.Primary.Large}>Filter</SubmitButton>
            <Button appearance={Buttons.Functional.Secondary.Large} testId="reset" onClick={resetForm}>
            Reset
            </Button>
          </Shelf>
        </Stack>
      )}
      {isMobile && (
        <Stack spacing="small">
          <SubmitButton appearance={Buttons.Functional.Primary.Large}>Filter</SubmitButton>
          <Stack.Spacer/>
          <Button appearance={Buttons.Functional.Secondary.Large} testId="reset" onClick={resetForm}>
            Reset
          </Button>
        </Stack>
      )}
    </Stack>
  );
};
